import React, { useState, useEffect, useRef } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Portal from '@mui/material/Portal';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import { Haptics, NotificationType } from '@capacitor/haptics';

export default function NotificationBar({status, setStatus}) {
    const [open, setOpen] = useState(true)
    const lastMessageRef = useRef('')
    const timerRef = useRef(null)
    const [durationMs, setDurationMs] = useState(3000)

    useEffect(() => {
        // Dedupe identical consecutive messages (basic)
        const currentKey = `${status.notificationType}|${status.message}`
        if (currentKey === lastMessageRef.current) {
            return;
        }
        lastMessageRef.current = currentKey;

        setOpen(true);

        // Per-type auto-hide durations tuned for mobile
        let autoHideMs = 3000;
        if (status.notificationType === 'success') autoHideMs = 2200;
        else if (status.notificationType === 'info') autoHideMs = 3500;
        else if (status.notificationType === 'error') autoHideMs = 8000; // keep errors longer
        setDurationMs(autoHideMs);

        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
            setStatus({ notificationType: 'neutral', message: '' });
            setOpen(false);
            timerRef.current = null;
        }, autoHideMs);

        // Haptics (best-effort; ignore if unavailable)
        (async () => {
            try {
                if (status.notificationType === 'success') {
                    await Haptics.notification({ type: NotificationType.SUCCESS });
                } else if (status.notificationType === 'error') {
                    await Haptics.notification({ type: NotificationType.ERROR });
                }
            } catch (_) {}
        })();

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [status, setStatus]);

    if (status.notificationType === 'success' || status.notificationType === 'error' || status.notificationType === 'info') {
        let color, title, iconColor;
        switch (status.notificationType) {
            case 'success':
                color = '#1e4620';
                title = 'Success';
                iconColor = '#4caf50';
                break;
            case 'error':
                color = '#4a2328';
                title = 'Error';
                iconColor = '#f44336';
                break;
            case 'info':
            default:
                color = '#1a2a3a';
                title = 'Info';
                iconColor = '#2196f3';
                break;
        }
        return (
            <Portal>
              <Collapse in={open}>
                <Box sx={{
                    position: 'fixed',
                    left: 12,
                    right: 12,
                    bottom: `max(12px, calc(env(safe-area-inset-bottom) + 8px))`,
                    zIndex: 4000,
                    width: 'auto',
                    minWidth: 0,
                    maxWidth: 480,
                    margin: '0 auto',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.25)',
                    borderRadius: 3,
                    animation: 'toastIn 240ms cubic-bezier(0.2, 0.6, 0.2, 1)',
                }}>
                    <Alert
                        severity={status.notificationType}
                        sx={{
                            bgcolor: color,
                            background: `linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02)), ${color}`,
                            color: '#fff',
                            borderRadius: 3,
                            border: '1px solid rgba(255,255,255,0.08)',
                            fontSize: '1rem',
                            fontWeight: 600,
                            boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
                            alignItems: 'center',
                            pr: 5,
                            pl: 2.5,
                            cursor: 'pointer',
                            position: 'relative',
                            backdropFilter: 'blur(6px)'
                        }}
                        iconMapping={{
                            success: <CheckCircleIcon sx={{ color: iconColor }} />, 
                            error: <ErrorIcon sx={{ color: iconColor }} />, 
                            info: <InfoIcon sx={{ color: iconColor }} />
                        }}
                        onClick={() => { setOpen(false); setStatus({ notificationType: 'neutral', message: '' }); }}
                        action={
                            <IconButton
                                aria-label="close"
                                size="large"
                                onClick={() => { setOpen(false); setStatus({ notificationType: 'neutral', message: '' }); }}
                                sx={{ color: iconColor, position: 'absolute', right: 6, top: 6, width: 36, height: 36 }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        }
                    >
                        <Box sx={{ position: 'absolute', top: 0, left: 0, height: 2, width: '100%', overflow: 'hidden', borderTopLeftRadius: 3, borderTopRightRadius: 3 }}>
                            <Box sx={{ height: '100%', width: '100%', background: `linear-gradient(90deg, ${iconColor} 0%, rgba(255,255,255,0.3) 100%)`, animation: 'progressShrink linear forwards', animationDuration: `${durationMs}ms` }} />
                        </Box>
                        <AlertTitle sx={{ color: iconColor, fontWeight: 800, fontSize: '1.05rem', mb: 0.25 }}>{title}</AlertTitle>
                        <span style={{ fontSize: '0.98rem', fontWeight: 600, lineHeight: 1.35 }}>{status.message}</span>
                    </Alert>
                </Box>
                <style>{`
                    @keyframes toastIn {
                        from { opacity: 0; transform: translateY(16px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes progressShrink {
                        from { transform: scaleX(1); transform-origin: left; }
                        to { transform: scaleX(0); transform-origin: left; }
                    }
                    @media (prefers-reduced-motion: reduce) {
                        .MuiBox-root { animation: none !important; }
                    }
                `}</style>
              </Collapse>
            </Portal>
        );
    } else {
        return null;
    }
}